import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, Card, Typography, Alert, Result, Spin } from 'antd';
import { LockOutlined, HeartOutlined } from '@ant-design/icons';
import authService from '../../services/authService';
import './AuthPages.scss';

const { Title, Text } = Typography;

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError(t('auth.resetPassword.noToken'));
        setValidating(false);
        return;
      }

      try {
        await authService.validateResetToken(token);
        setTokenValid(true);
      } catch (err) {
        setError(t('auth.resetPassword.invalidLink'));
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token, t]);

  const onFinish = async (values) => {
    setLoading(true);
    setError(null);

    try {
      await authService.resetPassword(token, values.password, values.password2);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card-wrapper">
            <Card className="auth-card" style={{ textAlign: 'center', padding: '60px 0' }}>
              <Spin size="large" />
              <Text style={{ display: 'block', marginTop: 16 }}>{t('auth.resetPassword.validating')}</Text>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card-wrapper bounce-in">
            <Card className="auth-card">
              <Result
                status="success"
                title={t('auth.resetPassword.successTitle')}
                subTitle={t('auth.resetPassword.successSubtitle')}
                extra={[
                  <Link to="/login" key="login">
                    <Button type="primary" size="large">
                      {t('auth.register.goToLogin')}
                    </Button>
                  </Link>
                ]}
              />
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card-wrapper bounce-in">
            <Card className="auth-card">
              <Result
                status="error"
                title={t('auth.resetPassword.invalidTitle')}
                subTitle={error || t('auth.resetPassword.invalidSubtitle')}
                extra={[
                  <Link to="/forgot-password" key="forgot">
                    <Button type="primary" size="large">
                      {t('auth.resetPassword.requestNewLink')}
                    </Button>
                  </Link>,
                  <Link to="/login" key="login">
                    <Button size="large">
                      {t('auth.forgotPassword.backToLogin')}
                    </Button>
                  </Link>
                ]}
              />
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card-wrapper bounce-in">
          <Card className="auth-card">
            <div className="auth-header">
              <div className="logo-section">
                <HeartOutlined className="logo-icon" />
                <Title level={2} className="auth-title">
                  {t('auth.resetPassword.title')}
                </Title>
                <Text className="auth-subtitle">
                  {t('auth.resetPassword.subtitle')}
                </Text>
              </div>
            </div>

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                closable
                onClose={() => setError(null)}
                style={{ marginBottom: 24 }}
              />
            )}

            <Form
              form={form}
              name="reset-password"
              onFinish={onFinish}
              layout="vertical"
              size="large"
              autoComplete="off"
            >
              <Form.Item
                name="password"
                label={t('auth.resetPassword.newPasswordLabel')}
                rules={[
                  { required: true, message: t('auth.resetPassword.newPasswordRequired') },
                  { min: 8, message: t('auth.register.passwordMinLength') },
                  {
                    pattern: /[A-Z]/,
                    message: t('auth.register.passwordUppercase'),
                  },
                  {
                    pattern: /[a-z]/,
                    message: t('auth.register.passwordLowercase'),
                  },
                  {
                    pattern: /[0-9]/,
                    message: t('auth.register.passwordNumber'),
                  },
                  {
                    pattern: /[!@#$%^&*(),.?":{}|<>]/,
                    message: t('auth.register.passwordSpecial'),
                  },
                ]}
                help={t('auth.register.passwordHelp')}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item
                name="password2"
                label={t('auth.resetPassword.confirmPasswordLabel')}
                dependencies={['password']}
                rules={[
                  { required: true, message: t('auth.register.confirmPasswordRequired') },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t('auth.register.passwordsDoNotMatch')));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  className="auth-button"
                >
                  {t('auth.resetPassword.submitButton')}
                </Button>
              </Form.Item>

              <div className="auth-footer">
                <Link to="/login" className="auth-link">
                  {t('auth.forgotPassword.backToLogin')}
                </Link>
              </div>
            </Form>
          </Card>

          <div className="auth-decoration">
            <div className="paw-print">🐾</div>
            <div className="paw-print">🐾</div>
            <div className="paw-print">🐾</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
